import { getCulture } from '@web-platform/shared-universal-utils-frontend-localisation';

import type { EnrichedPricingOptionBookingPanel } from '@web-platform/shared-universal-types/src/redux/selectors/enrichedPricingOptionBookingPanel';
import type {
  PaymentDiscountBookingPanel,
  PaymentDiscountsBookingPanel,
} from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/paymentDiscountsBookingPanel';

export const getValidDiscounts = (
  configValue: boolean,
  discounts: PaymentDiscountBookingPanel[] | undefined,
): PaymentDiscountBookingPanel[] | undefined => {
  if (!configValue) return undefined;

  if (!Array.isArray(discounts) || discounts.length === 0) return undefined;

  const namedDiscounts = discounts.filter((discount) => !!discount.discountProvider);

  if (namedDiscounts.length === 0) return undefined;

  const { market } = getCulture();

  if (market.toLowerCase() === 'br') {
    if (
      namedDiscounts.length === 1 &&
      namedDiscounts[0].discountAmount != null &&
      namedDiscounts[0].discountAmount > 0 &&
      namedDiscounts[0].finalPrice != null &&
      namedDiscounts[0].finalPrice > 0
    ) {
      return namedDiscounts;
    }

    return undefined;
  }

  if (market.toLowerCase() === 'kr') {
    const filteredDiscounts = namedDiscounts.filter(
      (discount) => discount.finalPrice != null && discount.finalPrice > 0,
    );
    if (filteredDiscounts.length > 0) {
      return filteredDiscounts;
    }
    return undefined;
  }

  if (market.toLowerCase() === 'in') {
    const filteredDiscounts = namedDiscounts.filter(
      (discount) => discount.discountAmount != null && discount.discountAmount >= 1,
    );
    if (filteredDiscounts.length > 0) {
      return filteredDiscounts;
    }
    return undefined;
  }

  return undefined;
};

export const getPricingOptionsWithValidDiscounts = <T extends EnrichedPricingOptionBookingPanel>(
  configValue: boolean,
  pricingOptions: T[] | undefined,
): T[] =>
  (pricingOptions ?? []).reduce<T[]>((options, option) => {
    const validDiscounts = getValidDiscounts(configValue, option.paymentDiscounts);
    return validDiscounts ? [...options, { ...option, paymentDiscounts: validDiscounts }] : options;
  }, []);

// Merges in each option's paymentDiscounts from the filter-independent source (e.g.
// getAvailablePaymentDiscountsForItineraryBookingPanel) instead of whatever paymentDiscounts
// the enriched pricing options already carry, then applies the same validity filtering as
// getPricingOptionsWithValidDiscounts (dropping 0/invalid discounts and options left with none).
// Use this when the currently-saved payment method filter must not affect the discount data
// (e.g. building the full discount set for an event payload, which then gets filtered
// separately against the about-to-be-saved selection).
export const getPricingOptionsWithAllDiscounts = <T extends EnrichedPricingOptionBookingPanel>(
  configValue: boolean,
  pricingOptions: T[] | undefined,
  allDiscounts: PaymentDiscountsBookingPanel | undefined,
): T[] => {
  const discountsByOptionId = new Map(
    (allDiscounts?.pricingOptions ?? []).map((option) => [option.id, option.paymentDiscounts]),
  );

  return getPricingOptionsWithValidDiscounts(
    configValue,
    pricingOptions?.map((option) => ({
      ...option,
      paymentDiscounts: discountsByOptionId.get(option.id) ?? option.paymentDiscounts,
    })),
  );
};
